import React from 'react';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { FormattedMessage, useIntl } from 'react-intl';

import { toast } from 'src/hooks/UseToast';
import { queryClient } from 'src/lib/react-query';
import SquareIcon from 'src/registry/components/blocks/SquareIcon';
import { DataTableNewEntryProps } from 'src/registry/components/table/types';
import { Button } from 'src/registry/ui/Button';
import {
    Dialog,
    DialogBody,
    DialogClose,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogSeparator,
    DialogTitle,
    DialogTrigger,
} from 'src/registry/ui/Dialog';
import { generalErrorMessages, generalMessages } from 'src/registry/utils/messages';
import { ICON_MAP } from 'src/utils/constants';
import { useNavigate, useParams } from 'react-router-dom';
import { PATH } from 'src/features/navigation/utils/constants';
import { ALLOW_LIST_TYPE, AllowList } from 'src/features/allowList/utils/types';
import { allowListMessages } from 'src/features/allowList/utils/allowListMessages';
import { useDeleteAllowList } from 'src/features/allowList/api/useDeleteAllowList';
import { LIST_ALLOW_LISTS_RESOURCE } from 'src/features/allowList/api/useListAllowLists';
import { useParking } from 'src/providers/ParkingProvider';

interface AllowListFormModalProps<TData> extends DataTableNewEntryProps<TData> {
    selectedAllowList: AllowList;
    children?: React.ReactNode;
}

export default function DeleteAllowListFormModal<TData>({
    selectedAllowList,
    semantics,
    children,
}: AllowListFormModalProps<TData>) {
    const [isModalOpen, setIsModalOpen] = React.useState(false);
    const { formatMessage } = useIntl();
    const { listId } = useParams();
    const { parkingId } = useParking();
    const navigate = useNavigate();

    const allowListType = selectedAllowList.type;

    const successTitle =
        allowListType === ALLOW_LIST_TYPE.WHITE
            ? allowListMessages.deleteWhiteListSuccess
            : allowListMessages.deleteBlackListSuccess;
    const successDescription =
        allowListType === ALLOW_LIST_TYPE.WHITE
            ? allowListMessages.deleteWhiteListSuccessDescription
            : allowListMessages.deleteBlackListSuccessDescription;

    const modalSubtitle =
        allowListType === ALLOW_LIST_TYPE.WHITE
            ? allowListMessages.deleteWhiteListSubtitle
            : allowListMessages.deleteBlackListSubtitle;
    const modalConfirmMessage =
        allowListType === ALLOW_LIST_TYPE.WHITE
            ? allowListMessages.confirmDeleteWhiteList
            : allowListMessages.confirmDeleteBlackList;

    const navigatePathOnDeletion = allowListType === ALLOW_LIST_TYPE.WHITE ? 'white-list' : 'black-list';

    const { mutate: deleteAllowList, isPending: isPendingDelete } = useDeleteAllowList({
        onSuccess: () => {
            void queryClient.invalidateQueries({ queryKey: [LIST_ALLOW_LISTS_RESOURCE] });
            setIsModalOpen(false);

            if (listId) {
                void navigate(`/${PATH.ADMINISTRATION}/${navigatePathOnDeletion}`);
            }
            toast({
                title: formatMessage(successTitle),
                description: formatMessage(successDescription, {
                    entry: selectedAllowList?.name,
                }),
            });
        },
        onError: (error) => {
            if (error?.response?.status !== 400) return;
            const errorDetails = error.response?.data?.errors[0]?.detail;
            const errorCode = formatMessage(generalErrorMessages.somethingWentWrong);
            const errorMessage = errorDetails || formatMessage(generalErrorMessages.unknownError);

            toast({
                title: errorCode,
                description: errorMessage,
                variant: 'destructive',
            });
        },
    });

    const onSubmit = () => deleteAllowList({ listId: selectedAllowList.id, parkingId });

    return (
        <Dialog modal open={isModalOpen} onOpenChange={setIsModalOpen}>
            {children && <DialogTrigger asChild>{children}</DialogTrigger>}
            <DialogContent>
                <DialogHeader>
                    <div className="flex flex-row gap-2">
                        <SquareIcon icon={ICON_MAP.DELETE} />
                        <div>
                            <DialogTitle>
                                <FormattedMessage
                                    id={generalMessages.deleteEntry.id}
                                    defaultMessage={generalMessages.deleteEntry.defaultMessage}
                                    values={{ entry: semantics?.entryName?.toLowerCase() }}
                                />{' '}
                            </DialogTitle>
                            <DialogDescription>
                                {formatMessage(modalSubtitle, {
                                    entry: selectedAllowList?.name,
                                })}
                            </DialogDescription>
                        </div>
                    </div>
                </DialogHeader>
                <DialogSeparator />
                <DialogBody>
                    <p className="text-gray-500">
                        {formatMessage(modalConfirmMessage, {
                            entry: selectedAllowList?.name,
                        })}
                    </p>
                </DialogBody>
                <DialogSeparator />
                <DialogFooter>
                    <DialogClose asChild>
                        <Button variant="outline" type="button" className="flex-1">
                            <FormattedMessage {...generalMessages.cancel} />
                        </Button>
                    </DialogClose>
                    <Button
                        variant="destructiveLight"
                        disabled={isPendingDelete}
                        type="submit"
                        onClick={onSubmit}
                        className="flex-1"
                    >
                        {isPendingDelete && (
                            <FontAwesomeIcon icon={ICON_MAP.SPINNER} className="mr-2 size-4 animate-spin" />
                        )}
                        <FormattedMessage
                            id={generalMessages.deleteEntry.id}
                            defaultMessage={generalMessages.deleteEntry.defaultMessage}
                            values={{ entry: semantics?.entryName?.toLowerCase() }}
                        />
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}
